import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  useTheme,
} from "@mui/material";
import { Visibility } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAlert } from "../contexts/AlertContext";
import CommonLoader from "../components/CommonLoader";
import useResponsive from "../hooks/useResponsive";
import { AxiosResponse, GetSafetyInstructionApi } from "../utils/urlConst";
import LocalStorageUtil from "../utils/localStorageUtil";
import { storageKeys } from "../utils/constants";
import apiService from "../apiService/ApiService";
import { SafetyItem } from "../interfaces/SafetyItems";
import { useNavigate } from "react-router-dom";

export default function SafetyInstructions() {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { isTablet, isMobile, isDesktop } = useResponsive();
  const [isLoading, setIsLoading] = useState(false);
  const [safetyDoc, setSafetyDocs] = useState<SafetyItem[] | null>([]);

  useEffect(() => {
    //call api on page load
    getSafetyDocs();
  }, []);

  //get safety documents from server
  const getSafetyDocs = async () => {
    try {
      setIsLoading(true);
      let userId = LocalStorageUtil.getItem(storageKeys.userId, "");
      let userToken = LocalStorageUtil.getItem(storageKeys.authToken, "");

      let jsondata = JSON.stringify({
        token: userToken,
        user_id: userId,
      });

      apiService.setAuthToken(userToken);

      const responseJson: AxiosResponse | null = await apiService.post(
        GetSafetyInstructionApi,
        jsondata
      );

      if (
        responseJson?.hasOwnProperty("status") &&
        responseJson?.status_code === 1
      ) {
        setIsLoading(false);
        setSafetyDocs(responseJson?.data);
      } else if (
        responseJson?.hasOwnProperty("status") &&
        (responseJson?.status_code === 2 || responseJson?.status_code === 0)
      ) {
        setIsLoading(false);
        LocalStorageUtil.clear();
        navigate("/login");
      } else {
        setIsLoading(false);
        showAlert(t("common.server_error"), "error");
      }
    } catch (error) {
      setIsLoading(false);
      showAlert(t("common.server_error"), "error");
    }
  };

  //on clicking eye icon, open the doc in new tab for preview.
  const handleDocumentSelect = (doc: SafetyItem) => {
    try {
      window.open(doc.link, "_blank");
    } catch (error) {
      showAlert(t("common.document_is_not_available"), "info");
    }
  };

  //safety list card item ui
  const safetyCard = () => {
    return (
      safetyDoc &&
      Array.isArray(safetyDoc) &&
      safetyDoc.map((doc, index) => (
        <ListItem
          key={doc.id}
          sx={{
            borderBottom: "1px solid #e0e0e0",
            "&:hover": { bgcolor: "#f5f5f5" },
            bgcolor: index % 2 === 0 ? "#F1F3FA" : "transparent",
          }}
        >
          <ListItemText
            sx={{
              fontFamily: "Nunito Sans SemiBold",
              color: theme.palette.blueColors.main,
              fontSize: isMobile ? "10px" : "14px",
            }}
            primary={doc.type}
          />
          <IconButton
            onClick={() => handleDocumentSelect(doc)}
            sx={{ color: theme.palette.primary.main }}
          >
            <Visibility />
          </IconButton>
        </ListItem>
      ))
    );
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: isMobile ? 3 : 5,
          py: 2,
          width: isDesktop ? "80%" : "100%",
        }}
      >
        <CommonLoader isVisible={isLoading} />
        <Typography
          gutterBottom
          fontFamily={"Nunito Sans Regular700"}
          sx={{
            color: theme.palette.blueColors.main,
            fontSize: isMobile ? '16px' : '20px',
          }}
        >
          Veiligheidsinstructies
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: isMobile ? 2 : 5,
          pb: 5,
        }}
      >
        {safetyDoc ? (
          <List
            disablePadding
            sx={{
              width: isDesktop ? "75%" : "auto",
              border: "1px solid #e0e0e0",
              display: "flex",
              flexDirection: "column",
              height: "auto",
              overflow: "hidden",
            }}
          >
            <ListItem sx={{ borderBottom: "1px solid #e0e0e0" }}>
              <ListItemText
                primary="Veiligheid Type"
                primaryTypographyProps={{
                  sx: {
                    color: theme.palette.blueColors.main,
                    fontFamily: "Nunito Sans SemiBold",
                    fontSize: "18px",
                  },
                }}
              />
              <Typography
                sx={{
                  color: theme.palette.blueColors.main,
                  fontFamily: "Nunito Sans SemiBold",
                  fontSize: "18px",
                }}
              >
                Actie
              </Typography>
            </ListItem>
            <Box sx={{ flex: 1, overflow: "hidden" }}>{safetyCard()}</Box>
          </List>
        ) : (
          <Box
            sx={{
              display: "flex",
              flex: 1,
              alignItems: "center",
              justifyContent: "center",
              height: 250,
            }}
          >
            <Typography
              sx={{
                fontFamily: "Nunito Sans Regular500",
                fontSize: "14px",
                color: theme.palette.blueColors.main,
              }}
            >
              {t("safetycard.no_records_available")}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
